import { useNavigate } from 'react-router-dom';

import type { Invitation, Notification } from '@perplexicon/api-client';

import {
  ActiveGameConflictDialog,
  Avatar,
  BlockConfirmButton,
  Button,
  Card,
  ConfirmActionButton,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  PageHeader,
  PageLayout,
  SectionHeading,
} from '../../components/index.js';
import { isActiveGameConflict, useActiveGameConflict } from '../../lib/active-game-conflict.js';
import { useBlockUser } from '../../lib/blocks.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDateTime } from '../../lib/format-date.js';
import {
  useAcceptFriendRequest,
  useDeclineFriendRequest,
  useIncomingFriendRequests,
} from '../../lib/friends.js';
import {
  useAcceptInvitation,
  useCancelInvitation,
  useDeclineInvitation,
  useIncomingInvitations,
  useOutgoingInvitations,
} from '../../lib/invitations.js';
import {
  useClearNotifications,
  useDeleteNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../lib/notifications.js';
import { notificationMessage } from './notification-message.js';

/**
 * The notifications screen (APP_SPEC §32, §65 "Platform"; docs/APP_PLAN.md
 * §12 M4) — and, since game invitations lost their own destination, the
 * one place invitations are acted on (APP_SPEC §20).
 *
 * An invitation notification is joined to its live invitation rather than
 * trusting the notification row: a notification is an immutable record of
 * something that happened, so it cannot know that the invitation was since
 * accepted, declined, cancelled, or expired. Matching against the incoming
 * list means Join/Decline appear only while the invitation is genuinely
 * actionable, and disappear on their own once it is not. The unique
 * partial index on `(session_id, invitee_id) where status = 'pending'`
 * guarantees at most one live invitation per session for this user, so the
 * match is unambiguous.
 *
 * No game-specific rendering exists here (CLAUDE.md §2) —
 * `notificationMessage` covers only the fixed, platform-owned notification
 * vocabulary.
 */
export function NotificationsPage() {
  const notifications = useNotifications();
  const incoming = useIncomingInvitations();
  const outgoing = useOutgoingInvitations();
  const incomingFriendRequests = useIncomingFriendRequests();
  const acceptFriendRequest = useAcceptFriendRequest();
  const declineFriendRequest = useDeclineFriendRequest();
  const blockUser = useBlockUser();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();
  const clearNotifications = useClearNotifications();
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();
  const cancel = useCancelInvitation();
  const conflict = useActiveGameConflict();
  const navigate = useNavigate();

  if (notifications.isPending) {
    return <LoadingState label="Loading notifications…" />;
  }

  if (notifications.isError) {
    return (
      <ErrorState
        message={getErrorMessage(notifications.error)}
        onRetry={() => void notifications.refetch()}
      />
    );
  }

  const liveInvitationsBySession = new Map<string, Invitation>(
    (incoming.data ?? []).map((invitation) => [invitation.sessionId, invitation]),
  );
  const liveFriendRequestsByUserId = new Map(
    (incomingFriendRequests.data ?? []).map((req) => [req.otherUser.id, req]),
  );
  const pendingSent = outgoing.data ?? [];
  const hasUnread = notifications.data.some((notification) => !notification.readAt);
  const hasNotifications = notifications.data.length > 0;

  function joinGame(invitationId: string) {
    accept.mutate(invitationId, {
      onSuccess: (session) => {
        void navigate(`/play/${session.id}`);
      },
      onError: (error) => conflict.capture(error, () => joinGame(invitationId)),
    });
  }

  /** The live invitation this notification is about, or `null` if it is no longer actionable. */
  function actionableInvitation(notification: Notification): Invitation | null {
    if (notification.type !== 'game.invitation_received') return null;
    if (!notification.sessionId) return null;
    return liveInvitationsBySession.get(notification.sessionId) ?? null;
  }

  /** The live friend request this notification is about, or `null` if it is no longer actionable. */
  function actionableFriendRequest(notification: Notification) {
    if (notification.type !== 'friend.request_received') return null;
    if (!notification.actor) return null;
    return liveFriendRequestsByUserId.get(notification.actor.id) ?? null;
  }

  return (
    <PageLayout>
      <PageHeader
        eyebrow="Dispatches"
        title="Notifications"
        description="Game invitations and updates from your fellow players."
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={!hasUnread}
              isLoading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </Button>
            {/*
              Clearing the history is destructive and unrecoverable, so it
              goes through the shared confirmation rather than firing on a
              single click. `mutateAsync` keeps the dialog open (and
              un-resubmittable) until the server has actually cleared the
              rows. The trigger is disabled outright when there is nothing
              to clear.
            */}
            <ConfirmActionButton
              triggerLabel="Clear all"
              triggerVariant="secondary"
              disabled={!hasNotifications}
              title="Clear all notifications?"
              description="This permanently deletes every notification in your history. Pending game invitations are not cancelled — you can still respond to them from the sender's invitation."
              confirmLabel="Clear all"
              destructive
              isPending={clearNotifications.isPending}
              onConfirm={() => clearNotifications.mutateAsync()}
            />
          </>
        }
      />

      {notifications.data.length === 0 ? (
        <EmptyState
          illustration="loadingSplash"
          title="No notifications"
          description="Game invitations and social updates will arrive here."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {notifications.data.map((notification) => {
            const invitation = actionableInvitation(notification);
            return (
              <li key={notification.id}>
                <Card
                  className={`relative flex flex-col gap-3 p-3 pr-12 tablet:flex-row tablet:items-center tablet:justify-between ${
                    notification.readAt ? '' : 'border-accent bg-surfaceElevated'
                  }`}
                >
                  {/*
                    Deleting one notification. `stopPropagation` keeps the
                    click off any primary/navigation handler the card grows
                    later, and the control sits outside the row's action
                    cluster so it never competes with Join/Decline.
                  */}
                  <button
                    type="button"
                    aria-label="Delete notification"
                    title="Delete notification"
                    disabled={
                      deleteNotification.isPending &&
                      deleteNotification.variables === notification.id
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteNotification.mutate(notification.id);
                    }}
                    className="absolute right-1 top-1 inline-flex min-h-touch min-w-touch items-center justify-center rounded-md text-textSecondary hover:bg-surfaceAlt hover:text-textPrimary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
                  >
                    <Icon name="delete" className="h-5 w-5" />
                  </button>
                  <div className="flex min-w-0 items-center gap-3">
                    {notification.actor && (
                      <Avatar
                        seed={notification.actor.id}
                        label={notification.actor.username}
                        size="sm"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm text-textPrimary">
                        {notificationMessage(
                          notification.type,
                          notification.actor?.username ?? null,
                        )}
                      </p>
                      <p className="text-xs text-textSecondary">
                        {formatDateTime(notification.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {invitation && (
                      <>
                        <Button
                          size="sm"
                          isLoading={accept.isPending && accept.variables === invitation.id}
                          disabled={decline.isPending}
                          onClick={() => joinGame(invitation.id)}
                        >
                          Join
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          isLoading={decline.isPending && decline.variables === invitation.id}
                          disabled={accept.isPending}
                          onClick={() => decline.mutate(invitation.id)}
                        >
                          Decline
                        </Button>
                      </>
                    )}
                    {(() => {
                      const friendRequest = actionableFriendRequest(notification);
                      if (friendRequest) {
                        return (
                          <>
                            <Button
                              size="sm"
                              isLoading={
                                acceptFriendRequest.isPending &&
                                acceptFriendRequest.variables === friendRequest.id
                              }
                              disabled={declineFriendRequest.isPending || blockUser.isPending}
                              onClick={() => acceptFriendRequest.mutate(friendRequest.id)}
                            >
                              Accept
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              isLoading={
                                declineFriendRequest.isPending &&
                                declineFriendRequest.variables === friendRequest.id
                              }
                              disabled={acceptFriendRequest.isPending || blockUser.isPending}
                              onClick={() => declineFriendRequest.mutate(friendRequest.id)}
                            >
                              Decline
                            </Button>
                            <BlockConfirmButton
                              username={friendRequest.otherUser.username}
                              isLoading={blockUser.isPending}
                              onConfirm={() => blockUser.mutate(friendRequest.otherUser.username)}
                            />
                          </>
                        );
                      }
                      return null;
                    })()}
                    {!notification.readAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        isLoading={markRead.isPending && markRead.variables === notification.id}
                        onClick={() => markRead.mutate(notification.id)}
                      >
                        Mark read
                      </Button>
                    )}
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {pendingSent.length > 0 && (
        <section className="mt-8">
          <SectionHeading>Invitations you sent</SectionHeading>
          <ul className="flex flex-col gap-2">
            {pendingSent.map((invitation) => (
              <li key={invitation.id}>
                <Card className="flex flex-col gap-3 p-3 tablet:flex-row tablet:items-center tablet:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar
                      seed={invitation.otherUser.id}
                      label={invitation.otherUser.username}
                      size="sm"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-textPrimary">
                        Waiting for {invitation.otherUser.username} to respond.
                      </p>
                      <p className="text-xs text-textSecondary">
                        Expires {formatDateTime(invitation.expiresAt)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={cancel.isPending && cancel.variables === invitation.id}
                    onClick={() => cancel.mutate(invitation.id)}
                  >
                    Cancel
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      )}

      {deleteNotification.isError && (
        <ErrorState message={getErrorMessage(deleteNotification.error)} />
      )}
      {clearNotifications.isError && (
        <ErrorState message={getErrorMessage(clearNotifications.error)} />
      )}
      {markRead.isError && <ErrorState message={getErrorMessage(markRead.error)} />}
      {markAllRead.isError && <ErrorState message={getErrorMessage(markAllRead.error)} />}
      {accept.isError && !isActiveGameConflict(accept.error) && (
        <ErrorState message={getErrorMessage(accept.error)} />
      )}
      {decline.isError && <ErrorState message={getErrorMessage(decline.error)} />}
      {cancel.isError && <ErrorState message={getErrorMessage(cancel.error)} />}
      {acceptFriendRequest.isError && (
        <ErrorState message={getErrorMessage(acceptFriendRequest.error)} />
      )}
      {declineFriendRequest.isError && (
        <ErrorState message={getErrorMessage(declineFriendRequest.error)} />
      )}
      {blockUser.isError && <ErrorState message={getErrorMessage(blockUser.error)} />}

      <ActiveGameConflictDialog
        conflict={conflict.conflict}
        onAbandon={conflict.abandonAndRetry}
        onDismiss={conflict.dismiss}
        isAbandoning={conflict.isAbandoning}
        abandonError={conflict.abandonError}
      />
    </PageLayout>
  );
}
